import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  format,
} from "date-fns";
import Swal from "sweetalert2";
import { API_BASE } from "config/api";

export default function useEventData() {
  const { church, user } = useAuth();
  const isGroupHead = ["department_head", "ministry_head"].includes(user?.role);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [ministries, setMinistries] = useState([]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    location: "",
    start_at: "",
    end_at: "",
    scope: "general",
    scope_department_id: "",
    scope_ministry_id: "",
    audience: "all",
    department_id: "",
    ministry_id: "",
    channels: [],
  });
  const [isSaving, setIsSaving] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);

  const calendarDays = [];
  for (let day = gridStart; day <= gridEnd; day = addDays(day, 1)) {
    calendarDays.push(day);
  }

  const fetchEvents = useCallback(async () => {
    if (!church?.id) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const params = new URLSearchParams({
        from: format(gridStart, "yyyy-MM-dd"),
        to: format(gridEnd, "yyyy-MM-dd"),
      });
      const response = await fetch(`${API_BASE}/events?${params.toString()}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setEvents(Array.isArray(result.data) ? result.data : []);
      setError(null);
    } catch (err) {
      console.error("Fetch events failed:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id, currentMonth]);

  const fetchAudienceOptions = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };
      const [deptRes, minRes] = await Promise.all([
        fetch(`${API_BASE}/departments/church/${church.id}`, { headers }),
        fetch(`${API_BASE}/ministries/church/${church.id}`, { headers }),
      ]);
      const deptJson = deptRes.ok ? await deptRes.json() : [];
      const minJson = minRes.ok ? await minRes.json() : [];
      setDepartments(Array.isArray(deptJson) ? deptJson : deptJson?.data || []);
      setMinistries(Array.isArray(minJson) ? minJson : minJson?.data || []);
    } catch (err) {
      console.error("Fetch audience options failed:", err);
    }
  }, [church?.id]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetchAudienceOptions();
  }, [fetchAudienceOptions]);

  const goToPrevMonth = () => setCurrentMonth((d) => subMonths(d, 1));
  const goToNextMonth = () => setCurrentMonth((d) => addMonths(d, 1));
  const goToToday = () => setCurrentMonth(new Date());

  const eventsForDay = useCallback(
    (day) => events.filter((e) => isSameDay(new Date(e.start_at), day)),
    [events]
  );

  const handleAddEvent = (day) => {
    setForm({
      title: "",
      description: "",
      location: "",
      start_at: format(day || new Date(), "yyyy-MM-dd'T'HH:mm"),
      end_at: "",
      scope: "general",
      scope_department_id: "",
      scope_ministry_id: "",
      audience: "all",
      department_id: "",
      ministry_id: "",
      channels: [],
    });
    setCreateModalOpen(true);
  };

  const toggleChannel = (channel) => {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel],
    }));
  };

  const handleCreateSubmit = async () => {
    try {
      if (!form.title.trim()) throw new Error("Give the event a title.");
      if (!form.start_at) throw new Error("Pick a start date/time.");

      setIsSaving(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const payload = {
        title: form.title,
        description: form.description || null,
        location: form.location || null,
        start_at: form.start_at,
        end_at: form.end_at || null,
      };

      if (!isGroupHead) {
        payload.department_id =
          form.scope === "department" ? form.scope_department_id || null : null;
        payload.ministry_id = form.scope === "ministry" ? form.scope_ministry_id || null : null;
      }

      if (form.channels.length > 0) {
        payload.notify = {
          audience: form.audience,
          department_id: form.audience === "department" ? form.department_id : null,
          ministry_id: form.audience === "ministry" ? form.ministry_id : null,
          channels: form.channels,
        };
      }

      const response = await fetch(`${API_BASE}/events`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      const body = await response.json();
      const notifyMsg =
        body.notify && form.channels.length > 0
          ? ` Notified ${body.notify.recipients} member(s).`
          : "";

      Swal.fire({ title: "Success!", text: `Event created.${notifyMsg}`, icon: "success" });
      fetchEvents();
      setCreateModalOpen(false);
    } catch (err) {
      console.error("Create event failed:", err);
      Swal.fire({ title: "Error!", text: err.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (event) => {
    setSelectedEvent(event);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/events/${selectedEvent.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({ title: "Success!", text: "Event deleted", icon: "success" });
      fetchEvents();
      setDeleteModalOpen(false);
    } catch (err) {
      console.error("Delete event failed:", err);
      Swal.fire({ title: "Error!", text: err.message, icon: "error" });
    }
  };

  return {
    currentMonth,
    monthStart,
    monthEnd,
    calendarDays,
    isLoading,
    error,
    goToPrevMonth,
    goToNextMonth,
    goToToday,
    eventsForDay,
    isSameMonth: (day) => isSameMonth(day, currentMonth),
    isToday: (day) => isSameDay(day, new Date()),
    events,
    departments,
    ministries,
    isGroupHead,
    createModalOpen,
    setCreateModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedEvent,
    form,
    setForm,
    toggleChannel,
    handleAddEvent,
    handleCreateSubmit,
    handleDelete,
    handleDeleteConfirm,
    isSaving,
  };
}
